import { authFetch, clearToken } from "../utils/auth";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft, Target, Clock, TrendingUp, Calendar, Dumbbell,
  BookOpen, Trash2, Users, BarChart3, Settings
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const API = window.location.origin;

const SKILL_COLORS = {
  putting: "bg-emerald-500/20 text-emerald-700",
  chipping: "bg-blue-500/20 text-blue-400",
  pitching: "bg-purple-500/20 text-purple-400",
  irons: "bg-amber-500/20 text-[#111]",
  driving: "bg-red-500/20 text-red-400",
  course: "bg-cyan-500/20 text-cyan-400"
};

export default function CoachStudentDetail() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- Only re-fetch on studentId change
  useEffect(() => { fetchStudent(); }, [studentId]);

  const fetchStudent = async () => {
    try {
      const res = await authFetch(`${API}/api/coach/students/${studentId}`);
      if (res.ok) {
        setStudent(await res.json());
      } else if (res.status === 403) {
        navigate("/dashboard");
      } else {
        toast.error("Student not found");
        navigate("/coach/students");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const unassignDrill = async (drillId) => {
    try {
      const res = await authFetch(`${API}/api/coach/assignments/${studentId}/${drillId}`, {
      });
      if (res.ok) {
        toast.success("Drill unassigned");
        fetchStudent();
      }
    } catch (e) {
      toast.error("Failed to unassign drill");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!student) return null;

  const totalHours = Math.round((student.total_practice_minutes || 0) / 60 * 10) / 10;
  const skillEntries = Object.entries(student.skill_breakdown || {}).sort((a, b) => b[1] - a[1]);
  const maxSkill = skillEntries.length > 0 ? skillEntries[0][1] : 1;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="px-6 py-4 border-b border-gray-200">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
              <Target className="w-5 h-5 text-[#111]" />
            </div>
            <div>
              <span className="font-bold text-lg text-[#111]">GolfFlowPro</span>
              <Badge className="ml-2 bg-purple-500/20 text-purple-400 border-purple-500/30">Coach</Badge>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => navigate("/coach/settings")} className="text-[#555] hover:text-[#111]">
            <Settings className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Nav */}
      <nav className="px-6 py-3 border-b border-gray-200 bg-gray-50">
        <div className="max-w-6xl mx-auto flex gap-1">
          {[
            { id: "dashboard", label: "Dashboard", icon: <Target className="w-4 h-4" />, path: "/coach" },
            { id: "students", label: "Students", icon: <Users className="w-4 h-4" />, path: "/coach/students" },
            { id: "library", label: "Drill Library", icon: <BookOpen className="w-4 h-4" />, path: "/coach/library" },
            { id: "analytics", label: "Analytics", icon: <BarChart3 className="w-4 h-4" />, path: "/coach/analytics" }
          ].map((tab) => (
            <Button key={tab.id} variant="ghost" onClick={() => navigate(tab.path)}
              className={`flex items-center gap-2 ${tab.id === "students" ? "bg-gray-200 text-[#111]" : "text-[#555] hover:text-[#111]"}`}>
              {tab.icon} {tab.label}
            </Button>
          ))}
        </div>
      </nav>

      <main className="px-6 py-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Back + Title */}
          <div>
            <Button variant="ghost" onClick={() => navigate("/coach/students")} className="text-[#555] hover:text-[#111] mb-2 -ml-2">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Students
            </Button>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-700 font-bold text-2xl">
                {student.name?.charAt(0) || "?"}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#111]" data-testid="student-name">{student.name}</h1>
                <p className="text-[#555] text-sm">
                  {student.handicap != null ? `Handicap ${student.handicap}` : "No handicap set"} &middot; {student.email}
                </p>
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "Sessions", value: student.sessions_completed || 0, icon: <Target className="w-4 h-4 text-emerald-700" />, color: "bg-emerald-500/20" },
              { label: "Streak", value: `${student.current_streak || 0} days`, icon: <TrendingUp className="w-4 h-4 text-blue-400" />, color: "bg-blue-500/20" },
              { label: "Practice Hours", value: totalHours, icon: <Clock className="w-4 h-4 text-purple-400" />, color: "bg-purple-500/20" },
              { label: "Days/Week", value: student.practice_days_per_week || 3, icon: <Calendar className="w-4 h-4 text-[#111]" />, color: "bg-amber-500/20" }
            ].map((stat, i) => (
              <motion.div key={stat.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Card className="bg-white border-gray-200">
                  <CardContent className="p-4">
                    <div className={`w-8 h-8 rounded-lg ${stat.color} flex items-center justify-center mb-2`}>{stat.icon}</div>
                    <p className="text-2xl font-bold text-[#111]">{stat.value}</p>
                    <p className="text-xs text-[#555]">{stat.label}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Skill Breakdown */}
            <Card className="bg-white border-gray-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-[#111] text-base">Skill Breakdown (30 days)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {skillEntries.map(([skill, count]) => (
                  <div key={skill} className="flex items-center gap-3">
                    <span className="text-sm text-[#555] w-20 capitalize">{skill}</span>
                    <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${SKILL_COLORS[skill]?.split(" ")[0]?.replace("/20", "") || "bg-gray-500"}`}
                        style={{ width: `${maxSkill > 0 ? (count / maxSkill) * 100 : 0}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-[#111] w-8 text-right">{count}</span>
                  </div>
                ))}
                {skillEntries.length === 0 && <p className="text-[#555] text-sm">No sessions logged yet</p>}
              </CardContent>
            </Card>

            {/* Assigned Drills */}
            <Card className="bg-white border-gray-200">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-[#111] text-base">Assigned Drills</CardTitle>
                <Button size="sm" variant="ghost" onClick={() => navigate("/coach/library")} className="text-emerald-700 hover:text-emerald-700 text-xs">
                  <Dumbbell className="w-3 h-3 mr-1" /> Assign More
                </Button>
              </CardHeader>
              <CardContent className="space-y-2">
                {(student.assigned_drills || []).length > 0 ? (
                  student.assigned_drills.map((drill) => (
                    <div key={drill.drill_id} className="flex items-center justify-between p-3 rounded-lg bg-gray-100">
                      <div>
                        <p className="text-sm font-medium text-[#111]">{drill.drill_name || drill.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={SKILL_COLORS[drill.category] || "bg-gray-500/20 text-[#555]"} variant="outline">
                            {drill.category}
                          </Badge>
                          <span className="text-xs text-[#555]">{drill.duration_minutes} min</span>
                          <Badge variant="outline" className={drill.priority === "required" ? "border-red-500/30 text-red-400" : "border-gray-500/30 text-[#555]"}>
                            {drill.priority}
                          </Badge>
                        </div>
                      </div>
                      <Button size="icon" variant="ghost" className="text-[#555] hover:text-red-400 shrink-0"
                        onClick={(e) => { e.stopPropagation(); unassignDrill(drill.drill_id); }}
                        data-testid={`unassign-drill-${drill.drill_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="text-[#555] text-sm py-4 text-center">No drills assigned yet</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Practice Log */}
          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-[#111] text-base">Recent Practice Log</CardTitle>
            </CardHeader>
            <CardContent>
              {(student.practice_log || []).length > 0 ? (
                <div className="space-y-2">
                  {student.practice_log.map((session, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-emerald-400" />
                        <div>
                          <p className="text-sm font-medium text-[#111]">{session.title || "Practice Session"}</p>
                          <p className="text-xs text-[#555]">
                            {session.date ? new Date(session.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "Unknown"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={SKILL_COLORS[session.skill] || "bg-gray-500/20 text-[#555]"} variant="outline">
                          {session.skill || "general"}
                        </Badge>
                        <span className="text-sm text-[#555]">{session.duration} min</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#555] text-sm py-6 text-center">No practice sessions recorded yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
